import { useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLanguage } from '../../../../../context/LanguageContext';

interface SelectedItemRowProps {
    label: string;
    onRemove?: () => void;
}

const ROW_SX = { display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, borderRadius: 0.5, '&:hover': { bgcolor: 'action.hover' } };
const LABEL_SX = { flexGrow: 1, minWidth: 0, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const REMOVE_BUTTON_SX = { p: 0.25, flexShrink: 0, color: 'text.secondary', '&:hover': { color: 'error.main' } };

function TruncatedItemLabel({ label }: { label: string }) {
    const ref = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [label]);

    const content = (
        <Typography ref={ref} variant="body2" sx={LABEL_SX}>
            {label}
        </Typography>
    );

    return isTruncated ? <Tooltip title={label} arrow>{content}</Tooltip> : content;
}

function SelectedItemRow({ label, onRemove }: SelectedItemRowProps) {
    const { t } = useLanguage();
    if (!onRemove) return <TruncatedItemLabel label={label} />;

    const removeLabel = t('pages.clients.queryBuilder.simple.selectionDialog.removeItem');
    return (
        <Box sx={ROW_SX}>
            <TruncatedItemLabel label={label} />
            <IconButton size="small" aria-label={`${removeLabel} ${label}`} title={removeLabel} onClick={onRemove} sx={REMOVE_BUTTON_SX}>
                <CloseIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
        </Box>
    );
}

export default SelectedItemRow;
